import React, { useEffect, useRef, useState } from 'react'

import { format, isBefore, parse } from 'date-fns'
import styled from 'styled-components'

import { AppPlugin } from '@api/local'
import {
  Button, Heading, Icon, IconEnum, Link, Paragraph, ResponsivePXValue,
  Spacer, theme,
} from '@client/components'
import { useConfig } from '@client/contexts/ConfigProvider'
import { useEvents } from '@contexts/GTMProvider'
import {
  useUserDetailsQuery, useUserCartQuery, RegisteredUserDetailsFragment, UserDetailsFragment,
  MealKitDishDetailsFragment, OrderFragment,
} from '@hooks/api'
import { useProductGroups } from '@hooks/UseProductGroups'
import { Modal, ModalLoading, MealKitModalCard, _handleWeekendBoxDeliveryText } from '@molecules/index'

const Container = styled.div`
  
  ${ResponsivePXValue('width', { mobile: '260px', tablet: '260px', desktop: '866px' })}
  align-items: center;
  justify-content: center;
  display: flex;

`
const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  ${ResponsivePXValue('max-width', { mobile: '260px', tablet: '260px', desktop: '750px' })}
`

const IconContainer = styled.div`
  ${ResponsivePXValue('width', { mobile: '50px', tablet: '50px', desktop: '86px' })}
  ${ResponsivePXValue('height', { mobile: '50px', tablet: '50px', desktop: '86px' })}
`

const MealKitCardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;

  > * {
    flex: 0 1 calc(25% - 12px); /* 4 per row, accounting for gap */
    box-sizing: border-box;
  }

  @media (max-width: 1024px) {
    > * {
      flex: 0 1 calc(33.333% - 11px); /* 3 per row */
    }
  }

  @media (max-width: 400px) {
    > * {
      flex: 0 1 calc(50% - 8px); /* 2 per row */
    }
  }

  
`

export interface SignUpSuccessModalProps {
  open?: boolean
  onClose?: () => void
  onCardSuccess?: (creditCardId: string) => void
  orderData?: OrderFragment

  actionButtonText?: string
  isOnDemandOrder?: boolean
  secondaryActionButtonText?: string
  className?: string
  checkoutFlowType?: string
  dontShowNavigation?: boolean
}

interface SignUpSuccessModalState {
  error: string
  redirect: string
  paymentId: string
  creditCardId: string
  hasClosed: boolean
}

const DEFAULT_STATE: SignUpSuccessModalState = {
  error: '',
  redirect: '',
  paymentId: '',
  creditCardId: '',
  hasClosed: false,
}

export function SignUpSuccessModal({ open, onClose, orderData, isOnDemandOrder, checkoutFlowType, dontShowNavigation }: SignUpSuccessModalProps): JSX.Element {

  const config = useConfig()
  const [state, setState] = useState<SignUpSuccessModalState>({ ...DEFAULT_STATE })
  const { data: userDetailsData, loading: detailsLoading } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const user = userDetailsData?.currentUser as UserDetailsFragment & RegisteredUserDetailsFragment
  const { data: userCartData, refetch: refetchCart } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const isMobile = AppPlugin.shared().isMobile()
  const event = useEvents()
  const hasTriggeredEvent = useRef(false)

  const currentCategory: string | null = userCartData?.currentUser?.activeMenu?.subscription?.category?.id || null
  const productGroupData = useProductGroups(currentCategory)

  const today = new Date()

  const _handleClose = (): void => {
    if (onClose) {
      onClose()
    }
  }

  function getNextOrTodayWednesday(): string {
    const now = new Date()
    const day = now.getDay()
    const hour = now.getHours()
    const baseDate = new Date(now)
    if (!(day === 3 && hour < 9)) {
      const daysUntilNextWednesday = (3 - day + 7) % 7 || 7
      baseDate.setDate(baseDate.getDate() + daysUntilNextWednesday)
    }

    return baseDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    })
  }

  const safeFormat = (date: string | Date, formatStr: string): string => {
    if (!date) return ''
    try {
      return format(new Date(date), formatStr)
    } catch {
      return ''
    }
  }

  const onDemandDispatchDate = safeFormat(orderData?.dispatchDate, 'cccc do MMM yyyy')
  const onDemandOrderId = orderData?.shortId
  const orderItems = orderData?.orderItems
  const weekendBoxes = orderItems?.filter(item => item?.product?.isWeekendBox === true)
  const occasionBoxes = orderItems?.filter(item => item?.product?.isOccasionBox === true)
  const onDemandItems = orderItems?.filter(item => item?.product?.__typename === 'FrozenMeal' || item?.product?.__typename === 'Wine')
  const hasOnlyWeekendBoxes = weekendBoxes?.length === orderItems?.length
  const hasOnlyOccasionBoxes = occasionBoxes?.length === orderItems?.length
  const hasOnlyOnDemandItems = onDemandItems?.length === orderItems?.length
  let onDemandOrderMessage = `Your order number is (${onDemandOrderId}).`
  let processedWeekendBoxDispatchDate = ''
  let processedOccasionBoxDispatchDate = ''
  let weekendBoxDispatchDate = ''
  let occasionBoxDispatchDate = ''
  let weekendBoxText = ''
  let occasionBoxText = ''
  let onDemandText = ''

  const _handleWeekendBoxDeliveryDate = (): string => {
    weekendBoxDispatchDate = _handleWeekendBoxDeliveryText()
    const thisYear = today.getFullYear()
    const inputWithYear = `${weekendBoxDispatchDate} ${thisYear}`
    const processedMarketBoxtThisYearDispatchDate = safeFormat(inputWithYear, 'cccc do MMM yyyy')

    const cleaned = processedMarketBoxtThisYearDispatchDate
      .replace(/^[A-Za-z]+ /, '')
      .replace(/(\d+)(st|nd|rd|th)/, '$1')
    const parsed = parse(cleaned, 'd MMM yyyy', new Date())
    const isInPast = isBefore(parsed, new Date())
    if (isInPast) {
      const nextYear = thisYear + 1
      const inputWithNextYear = `${weekendBoxDispatchDate} ${nextYear}`
      const processedMarketBoxNextYearDispatchDate = safeFormat(inputWithNextYear, 'cccc do MMM yyyy')
      processedWeekendBoxDispatchDate = processedMarketBoxNextYearDispatchDate
    } else {
      processedWeekendBoxDispatchDate = processedMarketBoxtThisYearDispatchDate
    }
    return processedWeekendBoxDispatchDate
  }

  const _handleOccasionBoxDeliveryDate = (): string => {
    occasionBoxDispatchDate = occasionBoxes?.[0]?.product?.deliveryDate
    processedOccasionBoxDispatchDate = safeFormat(occasionBoxDispatchDate, 'cccc do MMM yyyy')
    return processedOccasionBoxDispatchDate
  }

  if (weekendBoxes?.length > 0 || occasionBoxes?.length > 0 || onDemandItems?.length > 0) {
    if (hasOnlyWeekendBoxes) {
      weekendBoxText = _handleWeekendBoxDeliveryDate()
      onDemandOrderMessage += ` Your order will be dispatched on ${weekendBoxText}.`
    } else if (hasOnlyOccasionBoxes) {
      occasionBoxText = _handleOccasionBoxDeliveryDate()
      onDemandOrderMessage += ` Your order will be dispatched on ${occasionBoxText}.`
    } else if (hasOnlyOnDemandItems) {
      onDemandText = onDemandDispatchDate
      onDemandOrderMessage += ` Your order will be dispatched on ${onDemandText}.`
    } else {
      if (weekendBoxes?.length > 0) {
        weekendBoxText = _handleWeekendBoxDeliveryDate()
        onDemandOrderMessage += ` Your Weekend Box will be dispatched on ${weekendBoxText}.`
      }
      if (occasionBoxes?.length > 0) {
        occasionBoxText= _handleOccasionBoxDeliveryDate()
        onDemandOrderMessage += ` Your Occasion Box will be dispatched on ${occasionBoxText}.`
      }
      if (onDemandItems?.length > 0) {
        onDemandText = onDemandDispatchDate
        onDemandOrderMessage += ` Your On Demand order will be dispatched on ${onDemandText}.`
      }
    }
  }

  const mealKits = userCartData?.currentUser?.activeCart?.cartItems?.filter((item) => { return item.product.__typename === 'MealKit' }).map((item) => { return item.product as unknown as MealKitDishDetailsFragment })
  const billingDateText = `Billing Date On Wednesday, ${getNextOrTodayWednesday()}. at 9am`
  const loading = detailsLoading
  const loadingMessage = 'Fetching your information'
  const title = isOnDemandOrder
    ? `Thank you for your order, ${user?.firstName}!`
    : `Welcome, Chef ${user?.firstName}!`

  const mealKitAmount = mealKits?.length
  const confirmationMessage = isOnDemandOrder
    ? onDemandOrderMessage
    : `We've picked ${mealKitAmount} Meal Kits to start your first order.`

  const userId = userDetailsData?.currentUser?.id
  const userEmail = userDetailsData?.currentUser?.email

  useEffect(() => {
    if (!hasTriggeredEvent.current && productGroupData && !state.hasClosed && open) {
      hasTriggeredEvent.current = true
      event.hasConfirmedSubscriptionDetails(productGroupData, userId, userEmail, checkoutFlowType)

      if (isOnDemandOrder) {
        event.hasMadePurchase(
          'make_payment',
          '',
          [],
          orderData.id,
          orderData.grandTotalAmount,
          '',
          0,
          userId,
        )
      }
     
      refetchCart()
    }
  }, [productGroupData])

  return (
    <Modal
      className='signup-success-modal'
      allowScroll
      contentOverflow='initial'
      fullscreen={false}
      open={open}
      title={title}
      resolutionAdapter={true}
      allowBackgroundClose={true}
      popupDirection='horizontal'
      onClose={_handleClose}>
      <Container >
        <ContentContainer>

          <IconContainer>
            <Icon
              icon={IconEnum.CHECKMARK_CIRCLE}
              color={theme.colors.greens.fruitSalad}
            />
          </IconContainer>

          <Choose>
            <When condition={isMobile}>
              <Paragraph variant='p3' align='center' bold>{confirmationMessage}</Paragraph>
            </When>
            <Otherwise>
              <Heading variant='h4' align='center'>{confirmationMessage}</Heading>
            </Otherwise>
          </Choose>
          <Spacer mobile='8px' desktop='16px' />

          {mealKits?.length > 0 && (
            <MealKitCardGrid>
              {mealKits.map((mealKit, index) => (
                <MealKitModalCard key={index} mealKit={mealKit} isMobile />
              ))}
            </MealKitCardGrid>
          )}
          <Spacer mobile='8px' desktop='16px' />

          <If condition={!isOnDemandOrder}>
            <Choose>
              <When condition={isMobile}>
                <Paragraph variant='p3' align='center' bold>Change and Choose dishes until:</Paragraph>
                <Paragraph variant='p4' align='center' bold>{billingDateText}</Paragraph>
              </When>
              <Otherwise>
                <Heading variant='h4' align='center'>Change and Choose dishes until:</Heading>
                <Heading variant='h2' align='center'>{billingDateText}</Heading>
              </Otherwise>
            </Choose>
          </If>

          <Spacer mobile='8px' desktop='16px' />
          <Choose>
            <When condition={!dontShowNavigation}>
              <Button
                className='menu-action-button'
                variant={'primary'}
                loading={loading}
                disabled={loading}
                color='black'
                title={'Meal Kit Menu'}
                href={'/meal-kit'}
                onClick={_handleClose}
              />
            </When>
          </Choose>

          <Spacer mobile='8px' desktop='16px' />
          <Link variant='l2' decoration='underline' href={'/meal-kit'} > Close </Link>
          <Choose>
            <When condition={loading}>
              <ModalLoading message={loadingMessage} />
            </When>
          </Choose>
        </ContentContainer>
      </Container>
    </Modal>
  )
}
